import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, ClipboardList, Pencil, User } from "lucide-react";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import ACL_RELATIONSHIPS from "acl-relationships";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import AuditLog from "components/views/AuditLog";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import NotFoundItem from "routes/404/NotFoundItem";
import { RecordLoadError } from "routes/swoogo/overviewPrimitives";
import { swoogoRegistrantKeys } from "routes/swoogo/queryKeys";
import RegistrantEditForm from "routes/swoogo/RegistrantEditForm";
import RegistrantOverview from "routes/swoogo/RegistrantOverview";
import RegistrantTitleActions from "routes/swoogo/RegistrantTitleActions";
import SwoogoService from "services/Swoogo";

const NOT_FOUND_STATUS = 404;

const RegistrantDetail = () => {
  const { id } = useParams();
  // Numeric, so the key matches the one the actions invalidate with registrant.id.
  const registrantId = Number(id);

  const canRead = useAuthorized([ACL_RELATIONSHIPS.swoogo.readRegistrant]);
  const canLink = useAuthorized([ACL_RELATIONSHIPS.swoogo.linkContact]);
  const canEdit = useAuthorized([ACL_RELATIONSHIPS.swoogo.updateRegistrant]);
  const canReadAuditLog = useAuthorized([ACL_RELATIONSHIPS.auditLog.read]);

  const {
    data: registrant,
    isLoading,
    error,
  } = useQuery({
    queryKey: swoogoRegistrantKeys.detail(registrantId),
    queryFn: () => SwoogoService.getRegistrant(registrantId),
    enabled: canRead,
    retry: false,
  });

  usePageTitle(registrant?.name || `Registrant #${id}`);

  if (!canRead) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Access denied</EmptyTitle>
            <EmptyDescription>
              You don&apos;t have permission to view this registrant.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  if (error?.response?.status === NOT_FOUND_STATUS) {
    return <NotFoundItem />;
  }

  if (error) {
    return <RecordLoadError title="Could not load the registrant" />;
  }

  const sections = [
    {
      key: "overview",
      label: "Overview",
      icon: User,
      content: (
        <RegistrantOverview registrant={registrant} isLoading={isLoading} />
      ),
    },
    {
      key: "activity",
      label: "Activity Logs",
      icon: ClipboardList,
      content: (
        <AuditLog
          modelType="swoogo_registrant"
          modelId={registrantId}
          allowManualActivity={false}
        />
      ),
      hidden: !canReadAuditLog,
    },
    {
      key: "edit",
      label: "Edit",
      icon: Pencil,
      content: registrant ? (
        <RegistrantEditForm registrant={registrant} />
      ) : null,
      hidden: !canEdit,
      hideFromSidebar: true,
    },
  ];

  return (
    <SidebarDetailLayout
      sections={sections}
      basePath={`/swoogo/registrants/${id}`}
      defaultSection="overview"
      header="Registrant"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/swoogo/registrants"
          parentLabel="Registrants"
          current={registrant?.name || `Registrant #${id}`}
        />
      }
      title={registrant?.name || `Registrant #${id}`}
      titleActions={
        registrant && (canLink || canEdit) ? (
          <RegistrantTitleActions
            registrant={registrant}
            canLink={canLink}
            canEdit={canEdit}
          />
        ) : null
      }
    />
  );
};

export default RegistrantDetail;
